import api from "@/api";

const PUSH_TEST_SENT_KEY = "mmj_push_test_sent_v1";

function isElectronApp() {
  return typeof window !== "undefined" && window.JEWEL_FLOW_ENV?.isElectron === true;
}

function isStandaloneApp() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

function isAppleMobileSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAppleMobile =
    /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && /Mobile/i.test(ua));
  const isSafariEngine = /Safari/i.test(ua) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(ua);
  return isAppleMobile && isSafariEngine;
}

function isPushSupported() {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    (window.isSecureContext || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  );
}

export function canUsePushNotifications() {
  return !isElectronApp() && isPushSupported();
}

export function getPushPermissionState() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission || "default";
}

export async function requestNotificationPermissionNow() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission || "default";
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

async function sendOneTimePushTest(endpoint) {
  if (!endpoint) return;
  const cacheKey = `${PUSH_TEST_SENT_KEY}:${endpoint}`;
  if (localStorage.getItem(cacheKey) === "1") return;
  try {
    await api.post("/push/test");
    localStorage.setItem(cacheKey, "1");
  } catch {
    // Ignore test-push failures; background delivery can still work later.
  }
}

async function ensurePushSubscription({ requestPermission = false } = {}) {
  if (!canUsePushNotifications()) {
    return { ok: false, reason: "unsupported" };
  }

  if (isAppleMobileSafari() && !isStandaloneApp()) {
    return { ok: false, reason: "install_required" };
  }

  try {
    const configRes = await api.get("/push/config");
    const config = configRes?.data || {};
    if (!config.enabled || !config.public_key) {
      return { ok: false, reason: "disabled" };
    }

    await navigator.serviceWorker.register(`${process.env.PUBLIC_URL || ""}/jewelflow-push-sw.js`);
    const registration = await navigator.serviceWorker.ready;

    let permission = getPushPermissionState();
    if (permission === "default" && requestPermission) {
      permission = await requestNotificationPermissionNow();
    }
    if (permission !== "granted") {
      return { ok: false, reason: permission === "denied" ? "denied" : "permission_required" };
    }

    let subscription = await registration.pushManager.getSubscription();
    const createdNow = !subscription;
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      });
    }

    const subscriptionJson = subscription.toJSON();
    await api.post("/push/subscribe", {
      subscription: subscriptionJson,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      user_agent: navigator.userAgent || "",
      platform: navigator.platform || "",
    });

    if (createdNow) {
      await sendOneTimePushTest(subscriptionJson.endpoint);
    }
    return { ok: true, endpoint: subscriptionJson.endpoint, created_now: createdNow };
  } catch (error) {
    const detail =
      error?.response?.data?.detail ||
      error?.message ||
      "Unknown push subscription error";
    return { ok: false, reason: "error", detail };
  }
}

export async function initPushNotifications(user) {
  if (!user) return false;
  try {
    const result = await ensurePushSubscription({ requestPermission: true });
    return !!result.ok;
  } catch {
    return false;
  }
}

export async function requestPushNotificationsFromBell() {
  return ensurePushSubscription({ requestPermission: true });
}
